import classNames from "classnames";
import React from "react";

import "./Switch.scss";

/**
 * Props for the Switch component.
 *
 * @description The Switch component is a toggle switch for boolean settings.
 * It provides a visual on/off toggle with animated track and thumb.
 *
 * @property {boolean} [value=false] - The current state of the switch.
 *   - true: Switch is in the "on" position
 *   - false: Switch is in the "off" position
 * @property {(value: boolean) => void} [onToggle] - Toggle event handler.
 *   Called when the switch is clicked. Receives the new boolean value.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 *
 * @example
 * // Basic switch
 * <Switch
 *   value={isEnabled}
 *   onToggle={setIsEnabled}
 * />
 *
 * @example
 * // Switch with test ID
 * <Switch
 *   value={darkMode}
 *   onToggle={(newValue) => setDarkMode(newValue)}
 *   testId="dark-mode-switch"
 * />
 *
 * @example
 * // Switch in a settings panel
 * const [notifications, setNotifications] = useState(true);
 * <div>
 *   <span>Enable notifications</span>
 *   <Switch value={notifications} onToggle={setNotifications} />
 * </div>
 */
interface Props {
  value?: boolean;
  onToggle?: (value: boolean) => void;
  testId?: string;
}

/**
 * A toggle switch component for boolean settings.
 *
 * @description Switch renders a styled toggle button with an animated track and thumb.
 * It provides a visual on/off control for boolean settings, commonly used in
 * settings panels, preferences, and feature toggles.
 *
 * Features:
 * - Animated track and thumb for smooth transitions
 * - Visual on/off states with distinct styling
 * - Click-to-toggle interaction
 * - Accessible button element
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A button element styled as a toggle switch
 *
 * @see Props for all available properties
 */
export const Switch: React.FC<Props> = (props) => {
  const { value = false, onToggle, testId } = props;

  const wrapClass = classNames("switch-content", { on: value });

  const handleToggle = () => {
    if (onToggle) {
      onToggle(!value);
    }
  };

  return (
    <button className={wrapClass} onClick={handleToggle} data-testid={testId}>
      <div className="switch-content__track"></div>
      <div className="switch-content__thumb"></div>
    </button>
  );
};
